import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

import { hasCapability } from "@/lib/roles";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, ArrowUpDown, AlertTriangle, Pencil } from "lucide-react";
import { toast } from "sonner";


import EntitlementIssue from "./EntitlementIssue";
import { requestKey as movementRequestKey } from "@/lib/requestKey";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const movementReasonPlaceholder = (type) => ({ stock_in: "Reason for restocking...", issue: "Reason for issuing these items...", return: "Reason for returning these items...", damaged_lost: "Describe the damage or loss...", correction: "Reason for stock correction..." }[type] || "Reason for this movement...");
export const originalIssueLabel = (issue) => {
  const kind = issue.entitlement_kind === "extra" ? "EXTRA" : issue.entitlement_kind === "normal" ? "NORMAL" : "ORDINARY";
  const date = issue.at ? new Date(issue.at) : null;
  const timestamp = date && !Number.isNaN(date.getTime())
    ? new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h12", timeZone: "Africa/Lagos" }).format(date).replace(",", "").replace(/am|pm/g, text => text.toUpperCase())
    : "Date unavailable";
  return `${kind} · ${issue.quantity} ${issue.quantity === 1 ? "unit" : "units"} · ${timestamp} · Returnable: ${issue.unreturned}`;
};

const movementLabels = { stock_in: "Stock In / Restock", issue: "Issue", return: "Return", damaged_lost: "Damaged / Lost", correction: "Stock Correction" };
const empty = { name: "", category: "", sku: "", quantity: 0, unit: "pcs", reorder_level: 0, unit_cost: "" };


const metadata = (item = {}) => ({
  name: item.name || "",
  category: item.category || "",
  sku: item.sku || "",
  unit: item.unit || "pcs",
  reorder_level: item.reorder_level ?? 0,
  unit_cost: item.unit_cost ?? "",
  location: item.location || "",
  description: item.description || "",
  supplier: item.supplier || "",
  reference: item.reference || "",
});
const Inventory = () => {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adj, setAdj] = useState({ item_id: "", movement_type: "stock_in", quantity: "", reason: "", direction: "increase", recipient_type: "student", recipient_id: "", recipient_name: "" });

  const [recipients, setRecipients] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [savingMovement, setSavingMovement] = useState(false);
  const [entitlementStatus, setEntitlementStatus] = useState(null);
  const [returnIssues, setReturnIssues] = useState([]);
  const [returnNext, setReturnNext] = useState(null);
  const [historyItem, setHistoryItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [historySkip, setHistorySkip] = useState(0);
  const [showArchived, setShowArchived] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(metadata());
  const [savingEdit, setSavingEdit] = useState(false);

  const needsReference = (adj.movement_type === "return" && adj.recipient_type !== "none") || adj.movement_type === "issue" || adj.movement_type === "damaged_lost" || (adj.movement_type === "correction" && adj.direction === "decrease");
  const canCreate = hasCapability(user, "inventory.create");
  const canEdit = hasCapability(user, "inventory.edit");
  const canAdjust = hasCapability(user, "inventory.adjust");
  const canIssue = hasCapability(user, "inventory.issue");
  const canReceive = hasCapability(user, "inventory.receive") || canAdjust;
  const studentIssue = adj.movement_type === "issue" && adj.recipient_type === "student" && !!adj.recipient_id;
  useEffect(() => {
    setReturnIssues([]); setReturnNext(null);
    if (!adjOpen || adj.movement_type !== "return" || adj.recipient_type !== "student" || !adj.recipient_id) return;
    let alive = true;
    api.get(`/inventory/entitlements/students/${adj.recipient_id}/return-options`, { params: { item_id: adj.item_id } }).then(({ data }) => { if (alive) { setReturnIssues(data.items); setReturnNext(data.next_skip); } }).catch(e => toast.error(formatApiError(e)));
    return () => { alive = false; };
  }, [adjOpen, adj.item_id, adj.movement_type, adj.recipient_type, adj.recipient_id]);
  const canDelete = hasCapability(user, "inventory.delete");

  const load = async () => {
    try { const { data } = await api.get("/inventory", { params: { include_archived: showArchived } }); setList(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [showArchived]);


useEffect(() => {
    if (!adjOpen || !needsReference || adj.recipient_type === "other") return;
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/inventory/recipients", { params: { kind: adj.recipient_type, q: recipientSearch } });
        if (active) setRecipients(data);
      } catch (e) { if (active) toast.error(formatApiError(e)); }
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [adjOpen, adj.movement_type, adj.direction, needsReference, adj.recipient_type, recipientSearch]);

  const viewHistory = async (item, skip = 0) => {
    try {
      const { data } = await api.get(`/inventory/${item.id}/history`, { params: { skip } });
      setHistoryItem(item); setHistory(data); setHistorySkip(skip);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const archive = async (item) => {
    if (!window.confirm(`Archive ${item.name}? Its stock and history will be preserved.`)) return;
    try {
      await api.delete(`/inventory/${item.id}`);
      toast.success("Item archived"); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
const create = async () => {
    try {
      const payload = { ...form, quantity: Number(form.quantity), reorder_level: Number(form.reorder_level), unit_cost: form.unit_cost ? Number(form.unit_cost) : undefined };
      const { data } = await api.post("/inventory", payload);
      setList((l) => [data, ...l]); setOpen(false); setForm(empty); toast.success("Item added");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openAdjust = (item) => { setAdj({ item_id: item.id, movement_type: canReceive ? "stock_in" : "issue", request_key: movementRequestKey(), entitlement_extra: false, entitlement_approval_id: null, return_movement_id: null, quantity: "", reason: "", direction: "increase", recipient_type: "student", recipient_id: "", recipient_name: "" }); setRecipientSearch("");
setRecipients([]);
    setAdjOpen(true);
};

const openEdit = (item) => {
    setEditing(item);
    setEditForm(metadata(item));
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!editing) return;
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        reorder_level: Number(editForm.reorder_level),
        unit_cost: editForm.unit_cost === "" ? null : Number(editForm.unit_cost),
      };
      const { data } = await api.patch(`/inventory/${editing.id}`, payload);
      setList((items) => items.map((item) => (item.id === data.id ? data : item)));
      setEditOpen(false);
      toast.success("Item details updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingEdit(false);
    }
  };
const applyAdjust = async () => {
    if (studentIssue && (!entitlementStatus || entitlementStatus.blocked)) return toast.error("Check the student entitlement and approval before issuing");

if (!Number.isInteger(Number(adj.quantity)) || Number(adj.quantity) <= 0) return toast.error("Enter a positive whole quantity");
    if (needsReference && (adj.recipient_type === "other" ? !adj.recipient_name.trim() : !adj.recipient_id)) return toast.error("Select a recipient");
    setSavingMovement(true);
try {
      const { data } = await api.post("/inventory/adjust", {
        ...adj,
        quantity: Number(adj.quantity),
        recipient_type: needsReference ? adj.recipient_type : null,
        recipient_id: needsReference ? adj.recipient_id : null,
        recipient_name: needsReference ? adj.recipient_name : null,
      });
      setList((l) => l.map((i) => (i.id === data.id ? data : i)));
      setAdjOpen(false);
      toast.success("Stock adjusted");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSavingMovement(false); }
  };

  return (
    <div data-testid="inventory-page">
      <nav className="flex gap-4 mb-4 text-sm">{(canIssue || hasCapability(user,"inventory.entitlement.manage") || hasCapability(user,"inventory.entitlement.override") || hasCapability(user,"inventory.view_history")) && <a href="/app/inventory/entitlements">Termly Entitlements / Student Summary</a>}{hasCapability(user,"user.manage") && <a href="/app/users?inventory_access=1">Staff Access</a>}</nav>
      <PageHeader
        overline="Stores"
        title="Inventory Management"
        description="Track stock levels, reorder alerts and movements across all academy supplies."
        actions={canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="add-item-btn"><Plus className="w-4 h-4 mr-1.5" /> New Item</Button></DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Add Inventory Item</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 mt-2">
                <Fld label="Name" full><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name" /></Fld>
                <Fld label="Category"><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Fld>
                <Fld label="SKU"><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></Fld>
                <Fld label="Quantity"><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="item-qty" /></Fld>
                <Fld label="Unit"><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></Fld>
                <Fld label="Reorder Level"><Input type="number" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} /></Fld>
                <Fld label="Unit Cost (₦)"><Input type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></Fld>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={create} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="item-submit">Add Item</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />


<Label className="flex gap-2 mb-4"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived items</Label>
{list.length === 0 ? <EmptyState message="No items yet." /> : (
        <Card className="border-[#E2E8F0] overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-[#FDFBF7]">
              <TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>SKU</TableHead>
              <TableHead>Quantity</TableHead><TableHead>Reorder</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map(i => {
                const low = (i.quantity || 0) <= (i.reorder_level || 0);
                return (
                  <TableRow key={i.id} data-testid={`item-row-${i.id}`}>
                    <TableCell className="font-medium text-[#1B2C5A]">{i.name}</TableCell>
                    <TableCell>{i.category || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{i.sku || "—"}</TableCell>
                    <TableCell>{i.quantity} {i.unit}</TableCell>
                    <TableCell>{i.reorder_level}</TableCell>
                    <TableCell>
                      {(i.archived) ? <span className="text-slate-500">Archived</span>
                           : (low ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 inline-flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> Low
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                          OK
                        </span>
                      ))}
                    </TableCell>
                    <TableCell className="text-right">
                      {(canEdit && !i.archived) && <Button size="sm" variant="outline" onClick={() => openEdit(i)} data-testid={`item-edit-${i.id}`}><Pencil className="w-4 h-4 mr-1" /> Edit
                        </Button>}

{(canAdjust || canReceive || canIssue) && !i.archived && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openAdjust(i)}
                          data-testid={`item-adjust-${i.id}`}
                        >
                          <ArrowUpDown className="w-4 h-4 mr-1" /> Movement
                        </Button>
                      )}
                      {(hasCapability(user,"inventory.view_history") || hasCapability(user,"inventory.view")) && <Button size="sm" variant="outline" onClick={() => viewHistory(i)}>History</Button>}
                      {canDelete && !i.archived && <Button size="sm" variant="outline" onClick={() => archive(i)}>Archive</Button>}
</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="flex w-[calc(100%-2rem)] max-w-2xl max-h-[calc(100dvh-2rem)] flex-col overflow-hidden" data-testid="adjust-stock-modal">
          <DialogHeader className="shrink-0"><DialogTitle className="font-display text-[#1B2C5A]">Adjust Stock</DialogTitle></DialogHeader>
          <div className="grid min-h-0 min-w-0 gap-4 mt-2 break-words overflow-y-auto overflow-x-hidden pr-1 [&>*]:min-w-0 [&_select]:w-full [&_select]:min-w-0 [&_select]:max-w-full [&_select]:truncate [&_[role=combobox]]:min-w-0 [&_[role=combobox]>span]:truncate" data-testid="adjust-stock-scroll-body">
            <Fld label="Movement Type">
              <Select value={adj.movement_type} onValueChange={(movement_type) => { setEntitlementStatus(null); setAdj({ ...adj, movement_type, request_key: movementRequestKey(), entitlement_extra: false, entitlement_approval_id: null, return_movement_id: null }); }}>
                <SelectTrigger><SelectValue /></SelectTrigger><SelectContent>
                  {Object.entries(movementLabels).filter(([key]) => key === "issue" ? canIssue : ["stock_in", "return"].includes(key) ? canReceive : canAdjust).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            {adj.movement_type === "correction" && <Fld label="Correction Direction"><Select value={adj.direction} onValueChange={(direction) => setAdj({ ...adj, direction })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="increase">Increase stock</SelectItem><SelectItem value="decrease">Decrease stock</SelectItem></SelectContent></Select></Fld>}
            <Fld label="Quantity">
              <Input
                type="number"
                min="1" step="1"
                value={adj.quantity}
                onChange={(e) => setAdj({ ...adj, quantity: e.target.value })}
                data-testid="adjust-quantity"
              />
            </Fld>
            {(needsReference || adj.movement_type === "return") && <>
              <Fld label="Recipient Type"><Select value={adj.recipient_type} onValueChange={(recipient_type) => { setEntitlementStatus(null); setAdj({ ...adj, recipient_type, recipient_id: "", recipient_name: "", entitlement_extra: false, entitlement_approval_id: null, return_movement_id: null, request_key: movementRequestKey() }); setRecipients([]); setRecipientSearch(""); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{adj.movement_type === "return" && <SelectItem value="none">General return / no entitlement restoration</SelectItem>}{["student", "staff", "class", "department", "other"].map((kind) => <SelectItem key={kind} value={kind}>{kind === "class" ? "Class" : kind.charAt(0).toUpperCase() + kind.slice(1)}</SelectItem>)}</SelectContent></Select></Fld>
              {adj.recipient_type === "none" ? <p>General stock return; no student entitlement is restored.</p> : adj.recipient_type === "other" ? <Fld label="Recipient Name / Description"><Input value={adj.recipient_name} onChange={(e) => setAdj({ ...adj, recipient_name: e.target.value })} /></Fld> : <>
                <Fld label="Search existing recipients"><Input value={recipientSearch} onChange={(e) => setRecipientSearch(e.target.value)} /></Fld>
                <Fld label="Recipient"><Select value={adj.recipient_id} onValueChange={(recipient_id) => { setEntitlementStatus(null); setAdj({ ...adj, recipient_id, entitlement_extra: false, entitlement_approval_id: null, return_movement_id: null, request_key: movementRequestKey() }); }}><SelectTrigger><SelectValue placeholder="Select recipient" /></SelectTrigger><SelectContent>{recipients.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}{r.detail ? ` — ${r.detail}` : ""}</SelectItem>)}</SelectContent></Select></Fld>
              </>}
            </>}
            {studentIssue && <EntitlementIssue itemId={adj.item_id} studentId={adj.recipient_id} quantity={adj.quantity} reason={adj.reason} user={user} settings={adj} onSettings={fields => setAdj({ ...adj, ...fields })} onStatus={setEntitlementStatus} />}
            {adj.movement_type === "return" && adj.recipient_type === "student" && <label className="block min-w-0 w-full">Original student issue<select aria-label="Original student issue" className="block w-full min-w-0 max-w-full truncate rounded border p-2" value={adj.return_movement_id || ""} onChange={e => setAdj({ ...adj, return_movement_id: e.target.value || null })}><option value="">Select original issue</option>{returnIssues.map(m => <option key={m.id} value={m.id}>{originalIssueLabel(m)}</option>)}</select>{returnNext !== null && <Button variant="outline" onClick={async () => { try { const { data } = await api.get(`/inventory/entitlements/students/${adj.recipient_id}/return-options`, { params: { item_id: adj.item_id, skip: returnNext } }); setReturnIssues(rows => [...rows, ...data.items]); setReturnNext(data.next_skip); } catch (e) { toast.error(formatApiError(e)); } }}>Load Older Issues</Button>}</label>}
            <Fld label="Reason"><Input value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} placeholder={movementReasonPlaceholder(adj.movement_type)} /></Fld>
          </div>
          <DialogFooter className="shrink-0 gap-2" data-testid="adjust-stock-footer">
            <Button variant="outline" onClick={() => setAdjOpen(false)}>Cancel</Button>
            <Button
              onClick={applyAdjust}
              disabled={savingMovement || (studentIssue && (!entitlementStatus || entitlementStatus.blocked))}
              className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]"
              data-testid="adjust-submit"
            >Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

<Dialog open={Boolean(historyItem)} onOpenChange={(value) => { if (!value) setHistoryItem(null); }}><DialogContent className="max-w-5xl max-h-[85vh] overflow-auto"><DialogHeader><DialogTitle>{historyItem?.name} — History</DialogTitle></DialogHeader>
        <Table><TableHeader><TableRow>{["Date", "Movement", "Qty", "Recipient", "Previous", "New Balance", "Processed By", "Reason"].map((label) => <TableHead key={label}>{label}</TableHead>)}</TableRow></TableHeader><TableBody>{history.map((m) => <TableRow key={m.id}><TableCell>{m.at ? new Date(m.at).toLocaleString() : "—"}</TableCell><TableCell>{movementLabels[m.movement_type] || "Legacy adjustment"}{m.entitlement_kind ? ` · ${m.entitlement_kind.toUpperCase()}` : ""}</TableCell><TableCell>{m.quantity ?? m.delta}</TableCell><TableCell>{m.recipient_name || "—"}</TableCell><TableCell>{m.previous_quantity ?? "—"}</TableCell><TableCell>{m.new_quantity ?? "—"}</TableCell><TableCell>{m.by_name || m.processed_by || m.by}</TableCell><TableCell>{m.reason || "—"}</TableCell></TableRow>)}</TableBody></Table>
        {!history.length && <p>No stock movements recorded.</p>}
        <DialogFooter><Button variant="outline" disabled={!historySkip} onClick={() => viewHistory(historyItem, Math.max(0, historySkip - 100))}>Previous</Button><Button variant="outline" disabled={history.length < 100} onClick={() => viewHistory(historyItem, historySkip + 100)}>Next</Button></DialogFooter>
      </DialogContent></Dialog>
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A]">Edit Inventory Item</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 mt-2">
            <Fld label="Name" full><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="item-edit-name" /></Fld>
            <Fld label="Category"><Input value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} /></Fld>
            <Fld label="SKU"><Input value={editForm.sku} onChange={(e) => setEditForm({ ...editForm, sku: e.target.value })} /></Fld>
            <Fld label="Unit"><Input value={editForm.unit} onChange={(e) => setEditForm({ ...editForm, unit: e.target.value })} /></Fld>
            <Fld label="Reorder Level"><Input type="number" min="0" value={editForm.reorder_level} onChange={(e) => setEditForm({ ...editForm, reorder_level: e.target.value })} /></Fld>
            <Fld label="Unit Cost (₦)"><Input type="number" min="0" value={editForm.unit_cost} onChange={(e) => setEditForm({ ...editForm, unit_cost: e.target.value })} /></Fld>
            <Fld label="Location"><Input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} /></Fld>
            <Fld label="Supplier"><Input value={editForm.supplier} onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })} /></Fld>
            <Fld label="Reference" full><Input value={editForm.reference} onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })} /></Fld>
            <Fld label="Description" full><Input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></Fld>
          </div>
          <p className="text-xs text-[#475569]">Stock quantity can only be changed with the Adjust action.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="item-edit-submit">
              {savingEdit ? "Saving…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
</div>
  );
};

const Fld = ({ label, children, full }) => (
  <div className={"space-y-1.5 " + (full ? "col-span-2" : "")}><Label className="text-[#1B2C5A] text-sm">{label}</Label>{children}</div>
);

export default Inventory;
